import { useNavigate, useParams, useLocation } from "react-router-dom";
import "./Grades.scss";
import { errorToast, successToast } from "../../services/ToastHelper";
import { postApi, getApi } from "../../utils/apiService";
import { useEffect, useState, useMemo } from "react";
import dayjs from "dayjs";
import SubHeader from "../../components/SubHeader/SubHeader";
import { GET_ALL_GRADES, GET_ASSESSMENTTYPE_SCHEDULE } from "../../utils/apiPath";
import Loader from "../../components/Loader/Loader";
import ResourceCard from "../../components/ResourceCard/ResourceCard";
import ActionGroup from "../../components/ActionGroup/ActionGroup";
import ActionIcon from "../../components/ActionIcon/ActionIcon";
import CustomModal from "../../components/CustomModal/CustomModal";
import { EyeTwoTone, TeamOutlined, CopyOutlined, ClockCircleOutlined, CalendarOutlined, HomeOutlined, ScheduleOutlined } from "@ant-design/icons";
import { Dropdown, Empty } from "antd";
import { getStoredBranchOptions } from "../../services/auth";

const Grades = () => {
  const { id } = useParams();
  const location = useLocation();
  const { assessmentTypeName, subject } = location.state || {};

  const [isLoading, setIsLoading] = useState(false);
  const [gradesData, setGradesData] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const navigate = useNavigate();
  const role = sessionStorage.getItem("role");
  const gradeAccess = sessionStorage.getItem("gradeAccess");
  const branchId = sessionStorage.getItem("branchId");
  // Branches assigned to the staff; when more than one, the copy action lets them pick which branch's link to copy
  const staffBranches = useMemo(() => getStoredBranchOptions(), []);

  // Schedule modal state
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);
  const [scheduleGrade, setScheduleGrade] = useState(null);


  // parse grade access if stored
  const parsedGradeAccess = (() => {
    try {
      return JSON.parse(gradeAccess || "[]");
    } catch {
      return [];
    }
  })();
  const allowedGradeIds = role === "Staff" ? parsedGradeAccess.map((g) => g.gradeId) : null;

  const filteredGrades = useMemo(() => {
    if (role !== "Staff" || !allowedGradeIds) return gradesData;
    return gradesData.filter((grade) => allowedGradeIds.includes(grade.id));
  }, [gradesData, role, allowedGradeIds]);

  const fetchAllGrades = async () => {
    setIsLoading(true);
    const { statusCode, data, message } = await postApi(GET_ALL_GRADES);
    if (statusCode === 200) {
      setGradesData(data || []);
    } else {
      errorToast(message);
      console.error(message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchAllGrades();
  }, []);

  const handleViewModules = (record) => {
    navigate(`/modules/${id}/${record?.id}`, {
      state: {
        assessmentTypeName,
        subject,
        gradeName: record?.name || record?.grade_type,
      },
    });
  };

  const handleViewStudents = (record) => {
    navigate(`/students/${id}/${record?.id}`, {
      state: {
        assessmentTypeName,
        gradeName: record?.name || record?.grade_type,
      },
    });
  };

  // Build the assessment share link:
  // Base_Assessment_User / assessmentTypeId / gradeId / scheduleId (+ branchId when available)
  const buildAssessmentLink = (record, scheduleId, branchIdValue) => {
    let baseUrl = import.meta.env.VITE_BASE_ASSESSMENT_USER || import.meta.env.BASE_ASSESSMENT_USER || "www.meriian.assessment.com";
    baseUrl = baseUrl.replace(/^["']|["']$/g, '');

    const base = `${baseUrl}/${id}/${record?.id}/${scheduleId}`;
    const hasBranch = branchIdValue !== undefined && branchIdValue !== null && String(branchIdValue) !== "null" && String(branchIdValue) !== "";
    return hasBranch ? `${base}/${branchIdValue}` : base;
  };

  const copyAssessmentLink = (link) => {
    navigator.clipboard.writeText(link)
      .then(() => {
        successToast("Link copied to clipboard");
      })
      .catch((err) => {
        errorToast("Failed to copy link");
        console.error("Failed to copy link", err);
      });
  };

  // Format "2026-06-17" -> "17 Jun 2026"
  const formatScheduleDate = (d) => {
    if (!d) return "-";
    const parsed = dayjs(d);
    return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(d);
  };

  // Format "11:57:45.604Z" -> "11:57 AM" (no customParseFormat plugin available, so parse manually)
  const formatScheduleTime = (t) => {
    if (!t) return "-";
    const clean = String(t).replace("Z", "").split(".")[0]; // "11:57:45"
    const [hStr, m = "00"] = clean.split(":");
    let h = Number(hStr);
    if (!Number.isFinite(h)) return clean;
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  };

  // Fetch the schedule(s) for this assessment type (optionally for a specific branch) and open the modal
  const openScheduleModal = async (record, branchIdValue) => {
    setScheduleGrade(record);
    setScheduleData([]);
    setIsScheduleModalOpen(true);
    setScheduleLoading(true);

    const params = { assessmentTypeId: id, attendedOnly: false };
    const hasBranch = branchIdValue !== undefined && branchIdValue !== null && String(branchIdValue) !== "" && String(branchIdValue) !== "null";
    if (hasBranch) params.branchId = branchIdValue;

    const { statusCode, data, message } = await getApi(GET_ASSESSMENTTYPE_SCHEDULE, { params });
    if (statusCode === 200) {
      setScheduleData(Array.isArray(data) ? data : []);
    } else {
      errorToast(message);
    }
    setScheduleLoading(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="assessment">
      {isLoading && <Loader />}

      <SubHeader
        title="Grades"
        subTitle={subject ? `${assessmentTypeName} (${subject})` : assessmentTypeName}
        showBack={true}
        showRight={false}
        onBack={handleBack}
      />
      <div className="resource-grid">
        {filteredGrades.map((item) => (
          <ResourceCard
            key={item.id}
            title={item.name || item.grade_type}
            subTitle="Click to manage modules & syllabus"
            icon={<EyeTwoTone twoToneColor={["#0e82fd", "#e6f4ff"]} />}
            onClick={() => handleViewModules(item)}
            actions={
              <ActionGroup>
                {staffBranches.length > 1 ? (
                  <span
                    className="action-icon-dropdown"
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: "inline-flex" }}
                  >
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: staffBranches.map((b) => ({
                          key: b.value,
                          label: b.label,
                          onClick: ({ domEvent }) => {
                            domEvent?.stopPropagation();
                            openScheduleModal(item, b.value);
                          },
                        })),
                      }}
                    >
                      <button
                        type="button"
                        className="action-icon"
                        aria-label="Copy Link"
                        title="Copy Link"
                      >
                        <CopyOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                      </button>
                    </Dropdown>
                  </span>
                ) : (
                  <ActionIcon
                    title="Copy Link"
                    icon={<CopyOutlined style={{ fontSize: '18px', color: '#52c41a' }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openScheduleModal(item);
                    }}
                  />
                )}
                <ActionIcon
                  title="View Modules & Syllabus"
                  icon={<EyeTwoTone twoToneColor={["#0e82fd", "#e6f4ff"]} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewModules(item);
                  }}
                />
                <ActionIcon
                  title="View Students"
                  icon={<TeamOutlined style={{ fontSize: '18px', color: '#1890ff' }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewStudents(item);
                  }}
                />
              </ActionGroup>
            }
          />
        ))}
        {gradesData.length === 0 && !isLoading && (
          <div className="no-data">No grades found</div>
        )}
      </div>

      <CustomModal
        open={isScheduleModalOpen}
        title="Assessment Schedules"
        onClose={() => setIsScheduleModalOpen(false)}
        width={560}
        showPrimary={false}
        dangerText="Close"
      >
        {scheduleLoading ? (
          <div className="no-data">Loading schedules...</div>
        ) : scheduleData.length === 0 ? (
          <Empty description="No schedules found" />
        ) : (
          <div className="schedule-list">
            {scheduleData.map((s) => {
              // Branches this schedule applies to (response: branches[] / branchIds[])
              const scheduleBranches = Array.isArray(s.branches) && s.branches.length > 0
                ? s.branches
                : (s.branchIds || []).map((bid) => ({ branchId: bid, branchName: "" }));
              const branchNames = scheduleBranches.map((b) => b.branchName).filter(Boolean).join(", ");
              const singleBranchId = scheduleBranches[0]?.branchId;

              return (
                <div key={s.id} className="schedule-row">
                  <div className="schedule-info">
                    <span className="schedule-cell">
                      <CalendarOutlined /> {formatScheduleDate(s.scheduledDate)}
                    </span>
                    <span className="schedule-cell">
                      <ClockCircleOutlined /> {formatScheduleTime(s.scheduledTime)}
                    </span>
                    {s.academicYear && (
                      <span className="schedule-cell">
                        <ScheduleOutlined /> {s.academicYear}
                      </span>
                    )}
                    {branchNames && (
                      <span className="schedule-cell">
                        <HomeOutlined /> {branchNames}
                      </span>
                    )}
                  </div>
                  {scheduleBranches.length > 1 ? (
                    <span className="action-icon-dropdown" style={{ display: "inline-flex" }}>
                      <Dropdown
                        trigger={["click"]}
                        menu={{
                          items: scheduleBranches.map((b) => ({
                            key: b.branchId,
                            label: b.branchName || `Branch ${b.branchId}`,
                            onClick: () => copyAssessmentLink(buildAssessmentLink(scheduleGrade, s.id, b.branchId)),
                          })),
                        }}
                      >
                        <button type="button" className="action-icon" aria-label="Copy Link" title="Copy Link">
                          <CopyOutlined style={{ fontSize: "18px", color: "#52c41a" }} />
                        </button>
                      </Dropdown>
                    </span>
                  ) : (
                    <ActionIcon
                      title="Copy Link"
                      icon={<CopyOutlined style={{ fontSize: "18px", color: "#52c41a" }} />}
                      onClick={() => copyAssessmentLink(buildAssessmentLink(scheduleGrade, s.id, singleBranchId))}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default Grades;
